import { MdOutlineWebAsset, MdPhoneIphone, MdStorage,
         MdArchitecture, MdSupportAgent, MdTrendingUp } from "react-icons/md";
import { FaBriefcase, FaLaptopCode, FaHandshake } from "react-icons/fa";

const services = [
  {
    icon: <MdOutlineWebAsset size={26} />,
    title: "Full-Stack Web Development",
    description:
      "End-to-end web applications built with modern frameworks. Clean frontends, robust REST APIs, and scalable database backends.",
    tags: ["React.js", "Node.js", "REST APIs", "MySQL"],
    highlight: false,
  },
  {
    icon: <MdPhoneIphone size={26} />,
    title: "Mobile App Development",
    description:
      "Cross-platform mobile apps for Android and iOS with clean UI, offline support, push notifications, and full app store deployment.",
    tags: ["Flutter", "Firebase", "Play Store", "App Store"],
    highlight: true, // most popular
  },
  {
    icon: <MdStorage size={26} />,
    title: "Database Design",
    description:
      "Scalable relational and NoSQL schemas, query optimization, and data modelling tailored to your application needs.",
    tags: ["MySQL", "PostgreSQL", "Firebase", "SQL Server"],
    highlight: false,
  },
  {
    icon: <MdArchitecture size={26} />,
    title: "System Architecture",
    description:
      "Designing scalable, maintainable software systems, from API design patterns to deployment pipelines.",
    tags: ["OOP", "Design Patterns", "API Design", "Git"],
    highlight: false,
  },
  {
    icon: <MdSupportAgent size={26} />,
    title: "Technical Consulting",
    description:
      "Advisory for startups and businesses looking to scope, plan, or troubleshoot software projects. Clear guidance without the jargon.",
    tags: ["Project Scoping", "Code Review", "Tech Strategy"],
    highlight: false,
  },
  {
    icon: <MdTrendingUp size={26} />,
    title: "Data & Reporting",
    description:
      "Dashboards, reports, and data visualizations that turn raw data into clear, actionable insights for your team.",
    tags: ["Tableau", "Excel", "Microsoft Forms", "SharePoint"],
    highlight: false,
  },
];

const engagements = [
  {
    icon: <FaBriefcase size={20} className="text-cyan-400" />,
    title: "Full-Time Role",
    desc: "Looking for a dedicated in-house developer to own projects end-to-end.",
    cta: "View My Resume",
    target: "resume",
  },
  {
    icon: <FaLaptopCode size={20} className="text-violet-400" />,
    title: "Freelance Project",
    desc: "Need a specific feature, app, or website built on a project basis.",
    cta: "Start a Project",
    target: "contact",
    accent: "violet",
  },
  {
    icon: <FaHandshake size={20} className="text-amber-400" />,
    title: "Consulting",
    desc: "Need technical advice, architecture review, or a second opinion.",
    cta: "Book a Consultation",
    target: "contact",
    accent: "amber",
  },
];

const accentMap = {
  cyan:   { border: "border-cyan-500/30",   bg: "bg-cyan-500/10",   text: "text-cyan-400",   btn: "border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10" },
  violet: { border: "border-violet-500/30", bg: "bg-violet-500/10", text: "text-violet-400", btn: "border-violet-500/50 text-violet-400 hover:bg-violet-500/10" },
  amber:  { border: "border-amber-500/30",  bg: "bg-amber-500/10",  text: "text-amber-400",  btn: "border-amber-500/50 text-amber-400 hover:bg-amber-500/10" },
};

export default function Services() {
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="services" className="bg-navy-900 section-padding">
      <div className="container-max">

        {/* Header */}
        <div className="text-center mb-14">
          <span className="section-tag">What I Offer</span>
          <h2 className="section-title">Services</h2>
          <p className="section-sub">
            Available for freelance projects, consulting engagements, and full-time roles.
            Here is what I can build for you.
          </p>
        </div>

        {/* Service cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {services.map((s) => (
            <div key={s.title}
              className={`relative rounded-2xl p-6 flex flex-col group transition-all duration-300
                          ${s.highlight
                            ? "bg-cyan-500/5 border border-cyan-500/40 hover:border-cyan-400/70 hover:shadow-glow"
                            : "bg-navy-800 border border-navy-700 hover:border-cyan-500/30 hover:shadow-glow"}`}>

              {/* Popular badge */}
              {s.highlight && (
                <span className="absolute -top-3 left-5 bg-cyan-500 text-navy-950 text-xs font-bold
                                 px-3 py-1 rounded-full shadow-glow">
                  Most Requested
                </span>
              )}

              {/* Icon */}
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-colors
                               ${s.highlight
                                 ? "bg-cyan-500/20 text-cyan-400"
                                 : "bg-navy-700 text-cyan-400 group-hover:bg-cyan-500/10"}`}>
                {s.icon}
              </div>

              <h3 className="font-bold text-white text-sm mb-2">{s.title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed mb-4 flex-1">{s.description}</p>

              {/* Tags */}
              <div className="flex flex-wrap gap-1.5 mt-auto">
                {s.tags.map((tag) => (
                  <span key={tag}
                    className="text-xs px-2 py-0.5 rounded bg-navy-900 border border-navy-700 text-slate-500">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Engagement types */}
        <div className="mb-14">
          <p className="text-center text-xs font-semibold text-slate-500 uppercase tracking-widest mb-6">
            How we can work together
          </p>
          <div className="grid sm:grid-cols-3 gap-5">
            {engagements.map((e) => {
              const a = accentMap[e.accent ?? "cyan"];
              return (
                <div key={e.title}
                  className={`rounded-2xl border p-5 flex flex-col gap-3 transition-all duration-300
                               hover:shadow-glow ${a.border} bg-navy-800`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${a.bg}`}>
                    {e.icon}
                  </div>
                  <div>
                    <h4 className={`font-bold text-sm ${a.text}`}>{e.title}</h4>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">{e.desc}</p>
                  </div>
                  <button onClick={() => scrollTo(e.target)}
                    className={`mt-auto text-xs font-semibold px-4 py-2 rounded-lg border transition-colors ${a.btn}`}>
                    {e.cta}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA banner */}
        <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-8 md:p-10
                        flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-bold text-white mb-1">Ready to build something?</h3>
            <p className="text-sm text-slate-400 max-w-lg">
              Whether it is a mobile app, a web platform, or technical advice,
              I am available and ready to deliver. Let us talk.
            </p>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <button onClick={() => scrollTo("contact")} className="btn-primary whitespace-nowrap">
              Get In Touch
            </button>
            <button onClick={() => scrollTo("resume")} className="btn-outline whitespace-nowrap">
              View Resume
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}